import type { BusinessVerticalId } from "@/lib/business-verticals";
type SocialLinks = {
  instagram: string;
  facebook: string;
  tiktok: string;
  x: string;
  youtube: string;
  linkedin: string;
  pinterest: string;
  googleBusinessProfile: string;
};

type WeeklyHours = {
  days: string[];
  opens: string;
  closes: string;
};

type SiteAddress = {
  line1: string;
  city: string;
  state: string;
  zip: string;
};

export type PlatformOrderingConfig = {
  mode: "first_party" | "external";
  orderUrl: string;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
};

/**
 * SINGLE SOURCE OF TRUTH for the Google review numbers this platform is
 * allowed to publish as schema.org `AggregateRating`.
 *
 * Only set this on a location/branch whose numbers were read directly from the
 * Google Business Profile API. A location that omits the field emits NO
 * `aggregateRating` node at all — there is deliberately no default and no
 * fallback, because a self-asserted or guessed rating is a Google
 * manual-action risk, and because one host-aware build serves every tenant
 * (a shared default would paint one restaurant's rating onto another's pages).
 *
 * To refresh: `gbp` CLI (or the GBP API) against `gbpLocationId`, then update
 * `ratingValue`, `reviewCount`, and `verifiedOn` together. The same pair is
 * mirrored into the marketing copy in `src/lib/marketing-location-copy.ts`.
 */
export type VerifiedAggregateRating = {
  /** GBP `rating`, e.g. "4.7". String so JSON-LD serializes it verbatim. */
  ratingValue: string;
  /** GBP `reviewCount` for THIS location only, never a brand total. */
  reviewCount: string;
  /** GBP resource name, e.g. "locations/16277542901683087712". */
  gbpLocationId: string;
  /** ISO date the two numbers above were last read from the GBP API. */
  verifiedOn: string;
};

export type PlatformLocation = {
  id: string;
  slug: string;
  businessId?: string;
  tenantId?: string;
  restaurantId: string;
  ownerGroupId: string;
  name: string;
  alternateName?: string;
  legalName: string;
  domain: string;
  domains: string[];
  defaultBaseUrl: string;
  secondaryDomain: string;
  contact: {
    phone: string;
    email: string;
  };
  address: SiteAddress;
  reservationUrl: string;
  openTableUrl: string;
  yelpReservationUrl: string;
  yelpReservationWidgetUrl: string;
  /**
   * Canonical Google Maps entity URL (`?cid=`) for this location's GBP listing,
   * emitted as `hasMap` on the Restaurant node. Optional: tenants without a
   * confirmed CID simply omit the field and no `hasMap` is emitted.
   *
   * Deliberately NOT reusing `socials.googleBusinessProfile` — that value feeds
   * `sameAs` in three builders plus the footer and reviews UI, and is pinned by
   * tests/public-social-links.test.ts.
   */
  googleMapsUrl?: string;
  menuUrl: string;
  orderUrl: string;
  ordering: PlatformOrderingConfig;
  deliveryUrl?: string;
  jobsUrl: string;
  cateringUrl?: string;
  privatePartiesUrl?: string;
  socials: SocialLinks;
  ogImage: string;
  ogImageAlt: string;
  heroImage: string;
  heroVideo: string;
  logoImage: string;
  faviconImage?: string;
  faviconIcoImage?: string;
  favicon16Image?: string;
  appleTouchIconImage?: string;
  webAppIconImage?: string;
  twitterHandle: string;
  geo: {
    latitude: number;
    longitude: number;
  };
  hours: {
    open: string;
    close: string;
    weekly: WeeklyHours[];
  };
  rating: {
    value: string;
    count: string;
  };
  /**
   * GBP-verified rating for THIS location. Omit when unverified: the JSON-LD
   * builders emit no `aggregateRating` rather than a placeholder.
   */
  verifiedAggregateRating?: VerifiedAggregateRating;
  operator: {
    name: string;
    jobTitle: string;
    sameAs: string[];
  };
  source: {
    url: string;
    scrapedAt: string;
  };
};

/**
 * A secondary physical location of an existing restaurant tenant, modeled as
 * structured DATA only — NOT a domain-resolved tenant. It shares the parent
 * tenant's website, brand, and `restaurantId`; it is intentionally absent from
 * `locations` and from all host/tenant resolution. Consumed by location pages
 * and `buildBranchRestaurantJsonLd` to emit a Restaurant node that is
 * `branchOf` the primary `#restaurant`.
 */
export type RomaBranchLocation = {
  id: string;
  name: string;
  /**
   * Every other name this room is known by, including its exact GBP listing
   * title. Accepts an array because `name` carries the disambiguated form
   * ("... Henderson") while the GBP listing and most citations use the short
   * form — NAP consistency needs BOTH published on the node, not one or the
   * other. Consumed only by the JSON-LD builders, never interpolated into UI
   * copy, so an array is safe here.
   */
  alternateName?: string | string[];
  address: SiteAddress;
  phone: string;
  reservationUrl: string;
  /**
   * Canonical Google Maps entity URL (`?cid=`) for this branch's GBP listing.
   * Emitted as `hasMap`. Use the CID form, not a `search?q=` URL: the CID is a
   * stable pointer to the Maps entity and is the only unambiguous way to tell a
   * consumer which of two same-brand locations this node is.
   */
  googleMapsUrl?: string;
  /**
   * GBP-verified rating for THIS branch. Omit when unverified.
   */
  verifiedAggregateRating?: VerifiedAggregateRating;
  /**
   * Site-relative canonical page for this branch. A branch has no
   * `/locations/*` page, so its money page is the de facto canonical.
   *
   * This exists so EVERY builder that emits the branch's Restaurant node
   * resolves the same `url`. The homepage and the money pages both emit a node
   * under `#restaurant-<id>`; when they disagreed on `url` (homepage said the
   * homepage, money page said the money page) a consumer merging by `@id` saw
   * one entity making two contradictory canonical claims.
   */
  canonicalPath?: string;
  geo: {
    lat: number;
    lng: number;
  };
  hours: {
    open: string;
    close: string;
    weekly: WeeklyHours[];
  };
};

export type PlatformRestaurant = {
  id: string;
  slug: string;
  verticalId: BusinessVerticalId;
  ownerGroupId: string;
  name: string;
  alternateName?: string;
  legalName: string;
  defaultLocationId: string;
  locationIds: string[];
};

export type PlatformBusiness = PlatformRestaurant;
export type PlatformTenant = PlatformBusiness;

export type PlatformOwnerGroup = {
  id: string;
  name: string;
  primaryContactName: string;
};

export type RestaurantSiteConfig = Omit<
  PlatformLocation,
  | "id"
  | "slug"
  | "ownerGroupId"
  | "domains"
  | "defaultBaseUrl"
  | "source"
  | "deliveryUrl"
  | "cateringUrl"
  | "privatePartiesUrl"
> & {
  baseUrl: string;
  gtmId: string;
  gadsId: string;
  ga4Id: string;
  metaPixelId: string;
};

const ownerGroup: PlatformOwnerGroup = {
  id: "steven-kennedy-restaurants",
  name: "Steven Kennedy Restaurants",
  primaryContactName: "Steven Kennedy",
};

const fiaOwnerGroup: PlatformOwnerGroup = {
  id: "fia-hospitality",
  name: "FIA Hospitality Group",
  primaryContactName: "Mike",
};

const demoProfessionalServicesOwnerGroup: PlatformOwnerGroup = {
  id: "demo-professional-services",
  name: "Demo Professional Services",
  primaryContactName: "Platform Demo",
};

function normalizeMetaPixelId(pixelId: string | undefined): string {
  const trimmed = pixelId?.trim() ?? "";
  return /^\d{8,}$/.test(trimmed) ? trimmed : "";
}

function resolveMetaPixelId(
  location: PlatformLocation,
  env: Partial<NodeJS.ProcessEnv>,
): string {
  if (location.restaurantId === "roma-kitchen") {
    return normalizeMetaPixelId(
      env.NEXT_PUBLIC_ROMA_META_PIXEL_ID ??
        "1330113672558210",
    );
  }

  if (location.restaurantId === "tuscan-cove") {
    return normalizeMetaPixelId(env.NEXT_PUBLIC_TUSCAN_META_PIXEL_ID);
  }

  return "";
}

function resolveGa4Id(
  location: PlatformLocation,
  env: Partial<NodeJS.ProcessEnv>,
): string {
  if (location.restaurantId === "roma-kitchen") {
    return env.NEXT_PUBLIC_ROMA_GA4_ID?.trim() ?? "";
  }

  if (location.restaurantId === "fia-restaurant") {
    return env.NEXT_PUBLIC_FIA_GA4_ID?.trim() ?? "";
  }

  if (location.restaurantId === "tuscan-cove") {
    return (
      env.NEXT_PUBLIC_TUSCAN_GA4_ID?.trim() ??
      env.NEXT_PUBLIC_GA4_ID?.trim() ??
      env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim() ??
      ""
    );
  }

  return "";
}

const restaurants: PlatformRestaurant[] = [

  {
    id: "tuscan-cove",
    slug: "tuscan-cove",
    verticalId: "restaurant",
    ownerGroupId: ownerGroup.id,
    name: "Tuscan Cove Bar & Patio",
    legalName: "Tuscan Cove Bar & Patio",
    defaultLocationId: "tuscan-cove-southern-highlands",
    locationIds: ["tuscan-cove-southern-highlands"],
  },
  {
    id: "roma-kitchen",
    slug: "roma-kitchen",
    verticalId: "restaurant",
    ownerGroupId: ownerGroup.id,
    name: "Chef's Roma Kitchen",
    alternateName: "Roma Kitchen",
    legalName: "Roma Kitchen",
    defaultLocationId: "roma-kitchen-southern-highlands",
    locationIds: ["roma-kitchen-southern-highlands"],
  },
  {
    id: "fia-restaurant",
    slug: "fia-restaurant",
    verticalId: "restaurant",
    ownerGroupId: fiaOwnerGroup.id,
    name: "Fia Restaurant",
    legalName: "FIA Hospitality Group",
    defaultLocationId: "fia-restaurant-santa-monica",
    locationIds: ["fia-restaurant-santa-monica"],
  },
  {
    id: "acme-legal",
    slug: "acme-legal",
    verticalId: "law_firm",
    ownerGroupId: demoProfessionalServicesOwnerGroup.id,
    name: "Acme Legal Group",
    legalName: "Acme Legal Group",
    defaultLocationId: "acme-legal-main",
    locationIds: ["acme-legal-main"],
  },
  {
    id: "acme-barber",
    slug: "acme-barber",
    verticalId: "barber_shop",
    ownerGroupId: demoProfessionalServicesOwnerGroup.id,
    name: "Acme Barber Shop",
    legalName: "Acme Barber Shop",
    defaultLocationId: "acme-barber-main",
    locationIds: ["acme-barber-main"],
  },
  {
    id: "kennedy-legal-demo",
    slug: "kennedy-legal-demo",
    verticalId: "law_firm",
    ownerGroupId: "steven-kennedy-restaurants",
    name: "Kennedy Legal Demo",
    legalName: "Kennedy Legal Demo PLLC",
    defaultLocationId: "kennedy-legal-demo-main",
    locationIds: ["kennedy-legal-demo-main"],
  },
];

const locations: PlatformLocation[] = [


  {
    id: "tuscan-cove-southern-highlands",
    slug: "tuscan-cove-southern-highlands",
    restaurantId: "tuscan-cove",
    ownerGroupId: ownerGroup.id,
    name: "Tuscan Cove Bar & Patio",
    legalName: "Tuscan Cove Bar & Patio",
    domain: "tuscancove.com",
    domains: ["tuscancove.com", "www.tuscancove.com"],
    defaultBaseUrl: "https://tuscancove.com",
    secondaryDomain: "tuscancove.com",
    contact: {
      phone: "+1-725-223-0714",
      email: "steven@tuscancove.com",
    },
    address: {
      line1: "12656 Southern Highlands Pkwy",
      city: "Las Vegas",
      state: "NV",
      zip: "89141",
    },
    reservationUrl: "/reservations",
    openTableUrl: "/reservations",
    yelpReservationUrl:
      "https://www.yelp.com/reservations/tuscan-cove-bar-and-patio-las-vegas",
    yelpReservationWidgetUrl:
      "https://www.yelp.com/reservations/tuscan-cove-bar-and-patio-las-vegas/widget?orientation=compact&color-scheme=light",
    menuUrl: "/menu",
    orderUrl: "/order",
    ordering: {
      mode: "first_party",
      orderUrl: "/order",
      pickupEnabled: true,
      // Static CAPABILITY only: says this tenant supports delivery. Guests see
      // the option only when the runtime isDeliveryEnabled switch (admin
      // ordering settings) is also on — see composeOrderingState.
      deliveryEnabled: true,
    },
    jobsUrl: "https://tmt.spotapps.co/job-listings?spot_id=492492",
    socials: {
      instagram: "https://www.instagram.com/tuscancove",
      facebook: "https://www.facebook.com/p/Tuscancove-61575968813643/",
      tiktok: "",
      // X/Twitter profile omitted: x.com serves 404 to crawlers (Semrush flags
      // it as a broken link site-wide) and the brand is covered by IG/FB/LinkedIn/Pinterest.
      x: "",
      youtube: "",
      linkedin: "https://www.linkedin.com/company/tuscan-cove/",
      pinterest: "https://www.pinterest.com/tuscancove",
      googleBusinessProfile:
        "https://www.google.com/search?q=12656+Southern+Highlands+Parkway+Las+Vegas+Tuscan+Cove+Bar+%26+Patio",
    },
    ogImage:
      "https://static.spotapps.co/website_images/ab_websites/492492_website_v1/social_share.jpg",
    ogImageAlt:
      "Tuscan Cove Bar & Patio exterior and logo in Southern Highlands, Las Vegas",
    heroImage:
      "https://static.spotapps.co/spots/02/b566da6de740e19dcef9757aee532a/:original",
    heroVideo:
      "https://static.spotapps.co/website_videos/Tuscan%20Cove%20Bar%20%26%20Pati%20Video_Vimeo720p30.mp4",
    logoImage:
      "https://static.spotapps.co/website_images/ab_websites/492492_website_v1/logo.png",
    twitterHandle: "@tuscancove",
    geo: {
      latitude: 35.958902,
      longitude: -115.183607,
    },
    hours: {
      open: "16:00",
      close: "21:00",
      weekly: [
        {
          days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          opens: "16:00",
          closes: "21:00",
        },
  { days: ["Sunday"], opens: "10:00", closes: "20:00" },
      ],
    },
    rating: {
      value: "4.4",
      count: "36",
    },
    operator: {
      name: "Steven Kennedy",
      jobTitle: "Owner",
      sameAs: [
        "https://www.linkedin.com/in/steven-kennedy-7824a616/",
        "https://www.instagram.com/kennedy33lv/",
      ],
    },
    source: {
      url: "docs/tuscan-discovery/",
      scrapedAt: "2026-05-04",
    },
  },
  {
    id: "roma-kitchen-southern-highlands",
    slug: "roma-kitchen-southern-highlands",
    restaurantId: "roma-kitchen",
    ownerGroupId: ownerGroup.id,
    name: "Roma Kitchen",
    alternateName: "Chef's Roma Kitchen",
    legalName: "Roma Kitchen",
    domain: "chefsromakitchen.com",
    domains: [
      "chefsromakitchen.com",
      "www.chefsromakitchen.com",
      "roma-kitchen-lyonx.vercel.app",
    ],
    defaultBaseUrl: "https://chefsromakitchen.com",
    secondaryDomain: "chefsromakitchen.com",
    contact: {
      phone: "+1-702-888-3414",
      email: "info@chefsromakitchen.com",
    },
    address: {
      line1: "10420 S Decatur Blvd #100",
      city: "Las Vegas",
      state: "NV",
      zip: "89141",
    },
    reservationUrl:
      "https://www.google.com/maps/reserve/v/dine/c/WuQwA1k3kus",
    openTableUrl:
      "https://www.google.com/maps/reserve/v/dine/c/WuQwA1k3kus",
    yelpReservationUrl: "https://www.yelp.com/biz/roma-kitchen-las-vegas",
    // GBP locations/5937938347753510221 ("Roma Kitchen Southern Highlands"),
    // cid read from the live listing mapsUri 2026-08-02.
    googleMapsUrl: "https://maps.google.com/?cid=8194142417711379003",
    yelpReservationWidgetUrl:
      "https://www.yelp.com/reservations/roma-kitchen-las-vegas/widget?orientation=horizontal&color-scheme=light",
    menuUrl: "/menu",
    orderUrl:
      "https://grubhub.com/restaurant/roma-kitchen-10420-south-decatur-boulevard-las-vegas/11247504?classicAffiliateId=%2Fr%2Fw%2F11247504%2F&utm_source=restaurant.grubhub.com&utm_medium=OOL&utm_campaign=order%20online&utm_content=11247504",
    ordering: {
      mode: "external",
      orderUrl:
        "https://grubhub.com/restaurant/roma-kitchen-10420-south-decatur-boulevard-las-vegas/11247504?classicAffiliateId=%2Fr%2Fw%2F11247504%2F&utm_source=restaurant.grubhub.com&utm_medium=OOL&utm_campaign=order%20online&utm_content=11247504",
      pickupEnabled: false,
      deliveryEnabled: false,
    },
    deliveryUrl:
      "https://www.grubhub.com/restaurant/roma-kitchen-10420-south-decatur-boulevard-las-vegas/11247504?classicAffiliateId=%2Fr%2Fw%2F11247504%2Frestaurant%2F11247504",
    jobsUrl:
      "https://tmt.spotapps.co/job-listings?spot_id=101218&callback_url=http://chefsromakitchen.com/#",
    cateringUrl:
      "https://tmt.spotapps.co/catering?spot_id=101218&callback_url=http://chefsromakitchen.com/",
    privatePartiesUrl:
      "https://tmt.spotapps.co/private-parties?spot_id=101218&callback_url=http://chefsromakitchen.com/",
    socials: {
      instagram: "https://www.instagram.com/Chefsromakitchenlv",
      facebook: "https://www.facebook.com/chefsromakitchenlv",
      tiktok: "",
      x: "",
      youtube: "",
      linkedin: "",
      pinterest: "",
      // Canonical Maps entity (cid) for the Southern Highlands listing, NOT a
      // `search?q=` URL. A search URL is a query, not an entity: it resolves to
      // a results page, so it is worthless as a `sameAs` identity claim (and in
      // the footer it dropped guests on search results instead of the listing).
      // Same cid as `googleMapsUrl` above, read from the live GBP listing
      // mapsUri on 2026-08-02.
      googleBusinessProfile: "https://maps.google.com/?cid=8194142417711379003",
    },
    ogImage: "https://chefsromakitchen.com/images/social/chef-roma-og-lamb-chops.jpg",
    ogImageAlt:
      "Chef's Roma Kitchen lamb chops with Italian wine in a candlelit dining room",
    heroImage:
      "https://static.spotapps.co/website_images/ab_websites/101218_website/about_right.jpg",
    heroVideo: "",
    logoImage:
      "https://static.spotapps.co/website_images/ab_websites/101218_website/logo_new.png",
    faviconImage: "/images/brand/roma-favicon-32.png",
    faviconIcoImage: "/images/brand/roma-favicon.ico",
    favicon16Image: "/images/brand/roma-favicon-16.png",
    appleTouchIconImage: "/images/brand/roma-apple-touch-icon.png",
    webAppIconImage: "/images/brand/roma-favicon-512.png",
    twitterHandle: "",
    geo: {
      latitude: 36.00016626527835,
      longitude: -115.20674840441795,
    },
    hours: {
      open: "16:00",
      close: "21:00",
      weekly: [
        {
          days: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ],
          opens: "16:00",
          closes: "21:00",
        },
        { days: ["Sunday"], opens: "16:00", closes: "20:00" },
      ],
    },
    rating: {
      value: "",
      count: "",
    },
    // Read from the Google Business Profile API on 2026-08-03. This is the
    // Southern Highlands storefront only; the Henderson numbers live on the
    // `roma-kitchen-henderson` branch record below. Never publish the brand
    // total (449 across both) on a single-location Restaurant node.
    verifiedAggregateRating: {
      ratingValue: "4.7",
      reviewCount: "145",
      gbpLocationId: "locations/5937938347753510221",
      verifiedOn: "2026-08-03",
    },
    operator: {
      name: "Steven Kennedy",
      jobTitle: "Owner",
      sameAs: [
        "https://www.linkedin.com/in/steven-kennedy-7824a616/",
        "https://www.instagram.com/kennedy33lv/",
      ],
    },
    source: {
      url: "https://chefsromakitchen.com/",
      scrapedAt: "2026-05-23",
    },
  },
  {
    id: "fia-restaurant-santa-monica",
    slug: "fia-restaurant-santa-monica",
    restaurantId: "fia-restaurant",
    ownerGroupId: "fia-hospitality",
    name: "Fia Restaurant",
    legalName: "FIA Hospitality Group",
    domain: "fiarestaurant.com",
    domains: ["fiarestaurant.com","www.fiarestaurant.com"],
    defaultBaseUrl: "https://www.fiarestaurant.com",
    secondaryDomain: "fiarestaurant.com",
    contact: { phone: "+1-424-280-4196", email: "info@fiarestaurant.com" },
    address: { line1: "2454 Wilshire Boulevard", city: "Santa Monica", state: "CA", zip: "90403" },
    reservationUrl: "https://www.fiarestaurant.com/visit",
    openTableUrl: "https://www.fiarestaurant.com/visit",
    yelpReservationUrl: "https://www.yelp.com/biz/fia-restaurant-santa-monica",
    yelpReservationWidgetUrl: "",
    menuUrl: "/menu",
    orderUrl:
      "https://www.toasttab.com/local/fia-restaurant/r-0259b98d-cdad-4d1a-a561-af4f4a8e87ce",
    ordering: {
      mode: "external",
      orderUrl:
        "https://www.toasttab.com/local/fia-restaurant/r-0259b98d-cdad-4d1a-a561-af4f4a8e87ce",
      pickupEnabled: true,
      deliveryEnabled: true,
    },
    jobsUrl: "https://www.fiarestaurant.com/join-our-team",
    privatePartiesUrl: "https://www.fiarestaurant.com/private-dining/view-our-spaces",
    socials:       {
        "instagram": "https://www.instagram.com/fiarestaurant",
        "facebook": "",
        "tiktok": "",
        "x": "",
        "youtube": "",
        "linkedin": "",
        "pinterest": "",
        "googleBusinessProfile": ""
      },
    ogImage: "https://images.squarespace-cdn.com/content/v1/66db7fa56cb6773d51db6711/3030bb6f-60a6-4076-af5f-1996161f55e3/thumbnail_FIA+LOGO+-+RGB.png?format=1500w",
    ogImageAlt: "Fia Restaurant · Santa Monica, CA",
    heroImage: "https://images.squarespace-cdn.com/content/v1/66db7fa56cb6773d51db6711/884a0500-da5d-411f-8a2b-705d98522d13/2021-09-22-FiaSteak_interior-025.jpg",
    heroVideo: "",
    logoImage: "https://images.squarespace-cdn.com/content/v1/66db7fa56cb6773d51db6711/3030bb6f-60a6-4076-af5f-1996161f55e3/thumbnail_FIA+LOGO+-+RGB.png?format=1500w",
    twitterHandle: "",
    geo: { latitude: 34.0195, longitude: -118.4912 },
    hours:       {
        "open": "17:00",
        "close": "22:00",
        "weekly": [
          {
            "days": [
              "Sunday"
            ],
            "opens": "10:30",
            "closes": "21:00"
          },
  {
            "days": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday"
            ],
            "opens": "17:00",
            "closes": "21:00"
          },
  {
            "days": [
              "Friday"
            ],
            "opens": "17:00",
            "closes": "22:00"
          },
  {
    "days": [
      "Saturday"
    ],
    "opens": "10:30",
    "closes": "22:00"
  }
        ]
      },
    rating: { value: "", count: "" },
    operator: { name: "Mike", jobTitle: "Owner", sameAs: [] },
    source: { url: "https://www.fiarestaurant.com/", scrapedAt: "2026-06-06" },
  },
  {
    id: "acme-legal-main",
    slug: "acme-legal-main",
    businessId: "acme-legal",
    tenantId: "acme-legal",
    restaurantId: "acme-legal",
    ownerGroupId: demoProfessionalServicesOwnerGroup.id,
    name: "Acme Legal Group",
    legalName: "Acme Legal Group",
    domain: "acme-legal.example",
    domains: ["acme-legal.example", "www.acme-legal.example"],
    defaultBaseUrl: "https://acme-legal.example",
    secondaryDomain: "acme-legal.example",
    contact: {
      phone: "+1-702-555-0199",
      email: "intake@acme-legal.example",
    },
    address: {
      line1: "400 S 4th St Suite 500",
      city: "Las Vegas",
      state: "NV",
      zip: "89101",
    },
    reservationUrl: "/contact",
    openTableUrl: "",
    yelpReservationUrl: "",
    yelpReservationWidgetUrl: "",
    menuUrl: "/about",
    orderUrl: "/contact",
    ordering: {
      mode: "external",
      orderUrl: "/contact",
      pickupEnabled: false,
      deliveryEnabled: false,
    },
    jobsUrl: "/jobs",
    socials: {
      instagram: "",
      facebook: "",
      tiktok: "",
      x: "",
      youtube: "",
      linkedin: "https://www.linkedin.com/company/acme-legal-group-example",
      pinterest: "",
      googleBusinessProfile: "",
    },
    ogImage: "/images/og/tuscan-cove-og.jpg",
    ogImageAlt: "Acme Legal Group office in Las Vegas",
    heroImage: "/images/og/tuscan-cove-og.jpg",
    heroVideo: "",
    logoImage: "/images/brand/tuscan-cove-logo.png",
    twitterHandle: "",
    geo: {
      latitude: 36.167,
      longitude: -115.143,
    },
    hours: {
      open: "09:00",
      close: "17:00",
      weekly: [
        {
          days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "17:00",
        },
      ],
    },
    rating: {
      value: "",
      count: "",
    },
    operator: {
      name: "Platform Demo",
      jobTitle: "Managing Partner",
      sameAs: [],
    },
    source: {
      url: "platform-demo://acme-legal",
      scrapedAt: "2026-06-20",
    },
  },
  {
    id: "acme-barber-main",
    slug: "acme-barber-main",
    businessId: "acme-barber",
    tenantId: "acme-barber",
    restaurantId: "acme-barber",
    ownerGroupId: demoProfessionalServicesOwnerGroup.id,
    name: "Acme Barber Shop",
    legalName: "Acme Barber Shop",
    domain: "acme-barber.example",
    domains: ["acme-barber.example", "www.acme-barber.example"],
    defaultBaseUrl: "https://acme-barber.example",
    secondaryDomain: "acme-barber.example",
    contact: {
      phone: "+1-702-555-0144",
      email: "appointments@acme-barber.example",
    },
    address: {
      line1: "212 Fremont St",
      city: "Las Vegas",
      state: "NV",
      zip: "89101",
    },
    reservationUrl: "/contact",
    openTableUrl: "",
    yelpReservationUrl: "",
    yelpReservationWidgetUrl: "",
    menuUrl: "/about",
    orderUrl: "/contact",
    ordering: {
      mode: "external",
      orderUrl: "/contact",
      pickupEnabled: false,
      deliveryEnabled: false,
    },
    jobsUrl: "/jobs",
    socials: {
      instagram: "",
      facebook: "",
      tiktok: "",
      x: "",
      youtube: "",
      linkedin: "",
      pinterest: "",
      googleBusinessProfile: "",
    },
    ogImage: "/images/og/tuscan-cove-og.jpg",
    ogImageAlt: "Acme Barber Shop chairs and grooming station in Las Vegas",
    heroImage: "/images/og/tuscan-cove-og.jpg",
    heroVideo: "",
    logoImage: "/images/brand/tuscan-cove-logo.png",
    twitterHandle: "",
    geo: {
      latitude: 36.1699,
      longitude: -115.1398,
    },
    hours: {
      open: "10:00",
      close: "19:00",
      weekly: [
        {
          days: ["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          opens: "10:00",
          closes: "19:00",
        },
      ],
    },
    rating: {
      value: "",
      count: "",
    },
    operator: {
      name: "Platform Demo",
      jobTitle: "Owner",
      sameAs: [],
    },
    source: {
      url: "platform-demo://acme-barber",
      scrapedAt: "2026-06-20",
    },
  },
  {
    id: "kennedy-legal-demo-main",
    slug: "kennedy-legal-demo-main",
    businessId: "kennedy-legal-demo",
    tenantId: "kennedy-legal-demo",
    restaurantId: "kennedy-legal-demo",
    ownerGroupId: "steven-kennedy-restaurants",
    name: "Kennedy Legal Demo",
    legalName: "Kennedy Legal Demo PLLC",
    domain: "kennedy-legal-demo.local",
    domains: ["kennedy-legal-demo.local"],
    defaultBaseUrl: "https://kennedy-legal-demo.local",
    secondaryDomain: "kennedy-legal-demo.local",
    contact: { phone: "+1-702-555-0144", email: "intake@kennedy-legal-demo.local" },
    address: { line1: "400 S 4th St", city: "Las Vegas", state: "NV", zip: "89101" },
    reservationUrl: "/contact",
    openTableUrl: "",
    yelpReservationUrl: "",
    yelpReservationWidgetUrl: "",
    menuUrl: "/about",
    orderUrl: "",
    ordering: {
      mode: "external",
      orderUrl: "",
      pickupEnabled: false,
      deliveryEnabled: false,
    },
    jobsUrl: "",
    socials: {
      instagram: "",
      facebook: "",
      tiktok: "",
      x: "",
      youtube: "",
      linkedin: "",
      pinterest: "",
      googleBusinessProfile: "",
    },
    ogImage: "",
    ogImageAlt: "Kennedy Legal Demo office",
    heroImage: "",
    heroVideo: "",
    logoImage: "",
    twitterHandle: "",
    geo: { latitude: 36.1663, longitude: -115.1438 },
    hours:       {
        "open": "09:00",
        "close": "17:00",
        "weekly": []
      },
    rating: { value: "", count: "" },
    operator: { name: "Owner", jobTitle: "Owner", sameAs: [] },
    source: { url: "manual-demo", scrapedAt: "2026-06-20" },
  },
];

/**
 * Chef's Roma Kitchen secondary locations (branches). Henderson is a 2nd
 * physical location of the SAME website/brand (restaurantId "roma-kitchen"),
 * NOT a separate domain/tenant — it is intentionally absent from `locations`
 * and from host resolution. Consume via `buildBranchRestaurantJsonLd` and the
 * Roma money pages in Wave 2.
 *
 * Verified 2026-06-14 from the live GBP listing (locations/16277542901683087712):
 * geo from GBP latlng; hours = open daily 4 PM–2 AM (it is a bar/lounge — GBP
 * lists Bar/Lounge/Sports bar/Cocktail bar/Seafood as additional categories).
 */
export const romaBranchLocations: RomaBranchLocation[] = [
  {
    id: "roma-kitchen-henderson",
    name: "Chef's Roma Kitchen Henderson",
    // "Chef's Roma Kitchen" is the exact GBP listing title for this room (see
    // the locations/16277542901683087712 note below). It has to stay published
    // on the node: `name` is the disambiguated internal form, and dropping the
    // listing title would put the schema out of step with the GBP entity that
    // Google matches it against.
    alternateName: ["Roma Kitchen Henderson", "Chef's Roma Kitchen"],
    address: {
      line1: "1550 W Horizon Ridge Pkwy Ste P",
      city: "Henderson",
      state: "NV",
      zip: "89012",
    },
    phone: "+1-702-331-6300",
    reservationUrl:
      "https://www.google.com/maps/reserve/v/dine/c/GYmLWTH3LNU",
    // GBP locations/16277542901683087712 ("Chef's Roma Kitchen" Henderson),
    // cid read from the live listing mapsUri 2026-08-02.
    googleMapsUrl: "https://maps.google.com/?cid=4058887987554995451",
    // The Henderson money page (romaMoneyPages -> hendersonMoneyPage). Keep in
    // sync with that slug: it is the canonical `url` for every emission of the
    // `#restaurant-roma-kitchen-henderson` node.
    canonicalPath: "/best/italian-restaurant-henderson-nv",
    // Read from the Google Business Profile API on 2026-08-03. Henderson only;
    // Southern Highlands carries its own pair on the location record above.
    verifiedAggregateRating: {
      ratingValue: "4.7",
      reviewCount: "304",
      gbpLocationId: "locations/16277542901683087712",
      verifiedOn: "2026-08-03",
    },
    // From the live GBP listing latlng (2026-06-14).
    geo: {
      lat: 36.0183578,
      lng: -115.0542758,
    },
    hours: {
      // From the live GBP listing (2026-06-14): open daily 4 PM–2 AM.
      open: "16:00",
      close: "02:00",
      weekly: [
        {
          days: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          opens: "16:00",
          closes: "02:00",
        },
      ],
    },
  },
];

export const restaurantPlatform = {
  ownerGroup,
  restaurants,
  locations,
  defaultRestaurantId: "tuscan-cove",
  defaultLocationId: "tuscan-cove-southern-highlands",
} as const;

export const businessPlatform = {
  ownerGroup,
  businesses: restaurants,
  tenants: restaurants,
  locations,
  defaultBusinessId: restaurantPlatform.defaultRestaurantId,
  defaultTenantId: restaurantPlatform.defaultRestaurantId,
  defaultLocationId: restaurantPlatform.defaultLocationId,
} as const;

function normalizeHost(input: string | undefined): string {
  if (!input) return "";

  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return "";

  try {
    const url = trimmed.includes("://")
      ? new URL(trimmed)
      : new URL(`https://${trimmed}`);
    return url.hostname.replace(/^www\./, "");
  } catch {
    return trimmed.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0].split(":")[0];
  }
}

function canonicalizeBaseUrl(url: string, location: PlatformLocation): string {
  try {
    const parsedUrl = new URL(url);
    const host = normalizeHost(parsedUrl.hostname);
    const locationHosts = location.domains.map(normalizeHost);

    if (locationHosts.includes(host)) {
      parsedUrl.protocol = "https:";
      parsedUrl.hostname = location.domain;
    }

    return parsedUrl.toString().replace(/\/+$/, "");
  } catch {
    return location.defaultBaseUrl;
  }
}

export function getRestaurantBySlug(slug: string): PlatformRestaurant {
  const restaurant = restaurants.find(
    (candidate) => candidate.slug === slug || candidate.id === slug,
  );

  if (!restaurant) {
    throw new Error(`Unknown restaurant: ${slug}`);
  }

  return restaurant;
}

export function getBusinessBySlug(slug: string): PlatformBusiness {
  const business = businessPlatform.businesses.find(
    (candidate) => candidate.slug === slug || candidate.id === slug,
  );

  if (!business) {
    throw new Error(`Unknown business: ${slug}`);
  }

  return business;
}

export function getBusinessById(id: string): PlatformBusiness {
  return getBusinessBySlug(id);
}

export function getTenantById(id: string): PlatformTenant {
  return getBusinessBySlug(id);
}

export function getLocationBySlug(slug: string): PlatformLocation {
  const location = locations.find(
    (candidate) => candidate.slug === slug || candidate.id === slug,
  );

  if (!location) {
    throw new Error(`Unknown restaurant location: ${slug}`);
  }

  return location;
}

export function getLocationTenantId(location: PlatformLocation): string {
  return location.tenantId ?? location.businessId ?? location.restaurantId;
}

export function getLocationBusinessId(location: PlatformLocation): string {
  return location.businessId ?? location.tenantId ?? location.restaurantId;
}

export function resolveLocationsByTenantId(tenantId: string | undefined): PlatformLocation[] {
  if (!tenantId) return [];
  return businessPlatform.locations.filter(
    (location) => getLocationTenantId(location) === tenantId,
  );
}

export function resolveDefaultLocationForTenantId(
  tenantId: string | undefined,
): PlatformLocation | null {
  if (!tenantId) return null;
  const business = businessPlatform.businesses.find(
    (candidate) => candidate.id === tenantId,
  );
  if (!business) return null;
  return getLocationBySlug(business.defaultLocationId);
}

export function resolveLocationByHost(host: string | undefined): PlatformLocation {
  const normalizedHost = normalizeHost(host);

  // Host resolution only — do not couple theme flags to tenant selection.
  // Localhost / unknown preview hosts stay on the platform default (Tuscan).
  // Roma dark theme applies when brand is chefs-roma (path preview, Roma
  // domains, or remembered location cookie), not when the host is local.
  const matchedLocation = locations.find((location) =>
    location.domains.map(normalizeHost).includes(normalizedHost),
  );

  return matchedLocation ?? getLocationBySlug(restaurantPlatform.defaultLocationId);
}

function pathnameFromInput(input: string | undefined): string {
  if (!input) return "";

  try {
    const url = input.includes("://")
      ? new URL(input)
      : new URL(input, "https://example.com");
    return url.pathname;
  } catch {
    return input.split("?")[0]?.split("#")[0] ?? "";
  }
}

function decodeSlug(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function resolveLocationByPathname(
  pathname: string | undefined,
): PlatformLocation | null {
  const segments = pathnameFromInput(pathname).split("/").filter(Boolean);
  const locationsIndex = segments.indexOf("locations");
  const rawSlug = locationsIndex >= 0 ? segments[locationsIndex + 1] : undefined;

  if (!rawSlug) {
    return null;
  }

  const slug = decodeSlug(rawSlug);
  return (
    locations.find((location) => location.slug === slug || location.id === slug) ??
    null
  );
}

export function isDefaultLocation(location: Pick<PlatformLocation, "id">): boolean {
  return location.id === restaurantPlatform.defaultLocationId;
}

export function buildMapsHrefForLocation(location: PlatformLocation): string {
  const addressText = `${location.name} ${location.address.line1} ${location.address.city} ${location.address.state} ${location.address.zip}`;

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    addressText,
  )}`;
}

export function buildSiteConfigForLocation(
  location: PlatformLocation,
  options: {
    baseUrl?: string;
    env?: Partial<NodeJS.ProcessEnv>;
  } = {},
): RestaurantSiteConfig {
  const env = options.env ?? process.env;
  const baseUrl = canonicalizeBaseUrl(
    options.baseUrl ?? location.defaultBaseUrl,
    location,
  );
  const { deliveryUrl, cateringUrl, privatePartiesUrl, source, ...siteFields } = location;

  void deliveryUrl;
  void cateringUrl;
  void privatePartiesUrl;
  void source;

  return {
    ...siteFields,
    baseUrl,
    domain: baseUrl.replace(/^https?:\/\//, ""),
    gtmId: env.NEXT_PUBLIC_GTM_ID ?? "",
    gadsId: env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? "",
    ga4Id: resolveGa4Id(location, env),
    metaPixelId: resolveMetaPixelId(location, env),
  };
}

export function resolveSiteConfigFromEnv(
  env: Partial<NodeJS.ProcessEnv> = process.env,
): RestaurantSiteConfig {
  const configuredBaseUrl = (
    env.NEXT_PUBLIC_SITE_URL ??
    env.NEXT_PUBLIC_APP_URL ??
    env.SITE_URL ??
    getLocationBySlug(restaurantPlatform.defaultLocationId).defaultBaseUrl
  ).replace(/\/+$/, "");
  const location = resolveLocationByHost(configuredBaseUrl);

  return buildSiteConfigForLocation(location, {
    baseUrl: configuredBaseUrl,
    env,
  });
}

export function resolveSiteConfigForPathname(
  pathname: string | undefined,
  env: Partial<NodeJS.ProcessEnv> = process.env,
): RestaurantSiteConfig {
  const location = resolveLocationByPathname(pathname);

  if (location) {
    return buildSiteConfigForLocation(location, { env });
  }

  return resolveSiteConfigFromEnv(env);
}
